/**
 * Referral schemas
 */
import { z } from "zod";
import { createEntityFilterSchema, CurrencyCodeSchema, FieldsParamsSchema, NestedPaginationParamsSchema, ReferrerTypeSchema, SortParamsSchema, StrapiIdSchema, } from "./common.schemas";
import { LazyBillingDataRelation, LazyCommissionRelation, LazyDiscountRelation, LazyReferralCodeRelation, LazyReferralRelation, LazyUserRelation, PayoutRelationSchema, ReferralCodeRelationSchema, ReferralRelationSchema, } from "./relations";
// ============================================================================
// Enums
// ============================================================================
/**
 * Reward status schema
 */
export const RewardStatusSchema = z.enum(["pending", "available", "claimed", "expired"]);
/**
 * Payout status schema
 */
export const PayoutStatusSchema = z.enum(["pending", "processing", "completed", "failed"]);
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * Referral filters schema
 */
export const ReferralFiltersSchema = createEntityFilterSchema(ReferralRelationSchema, {
    scalars: ["expired"],
    relations: ["referrer", "referee", "discount"],
});
/**
 * Referral query params schema
 */
export const ReferralQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .merge(FieldsParamsSchema)
    .extend({
    filters: ReferralFiltersSchema.optional(),
})
    .strict();
/**
 * Referral code query params schema
 */
export const ReferralCodeQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: z
        .object({
        code: z.object({ $eq: z.string().optional() }).strict().optional(),
        "referrer.id": z.object({ $eq: StrapiIdSchema.optional() }).strict().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Referral code create input schema
 */
export const ReferralCodeCreateInputSchema = z
    .object({
    code: z.string().min(1, "Referral code is required").max(50),
    name: z.string().optional(),
})
    .strict();
/**
 * Claim discount reward input schema
 */
export const ClaimDiscountRewardInputSchema = z
    .object({
    rewardId: StrapiIdSchema,
})
    .strict();
/**
 * Claim payout reward input schema
 */
export const ClaimPayoutRewardInputSchema = z
    .object({
    amount: z.number().positive().optional(),
    paymentMethod: z.enum(["bank_transfer", "crypto"]).optional(),
    walletAddress: z.string().optional(),
})
    .strict();
/**
 * Payout status enum for filters (includes all possible statuses)
 */
const PayoutFilterStatusSchema = z.enum(["pending", "processing", "processed", "failed", "cancelled", "expired"]);
/**
 * Payout query params schema
 */
export const PayoutQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: z
        .object({
        pm_status: z
            .object({
            $eq: PayoutFilterStatusSchema.optional(),
            $in: z.array(PayoutFilterStatusSchema).optional(),
        })
            .strict()
            .optional(),
        pm_user: z.object({ id: StrapiIdSchema }).strict().optional(),
        requestedAt: z
            .object({
            $gte: z.string().optional(),
            $lte: z.string().optional(),
        })
            .strict()
            .optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Change partner type input schema
 */
export const ChangePartnerTypeInputSchema = z
    .object({
    userId: StrapiIdSchema,
    referrerType: ReferrerTypeSchema,
})
    .strict();
/**
 * Stats per referrer params schema
 */
export const StatsPerReferrerParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: z
        .object({
        referrerType: z.object({ $eq: ReferrerTypeSchema.optional() }).strict().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * Referral schema - extends ReferralRelationSchema with additional relations
 */
export const ReferralSchema = ReferralRelationSchema.extend({
    // Additional relations (populated)
    referee: LazyUserRelation.nullish(),
    commissions: z.array(LazyCommissionRelation).nullish(),
    discount: LazyDiscountRelation.nullish(),
    referral_code: LazyReferralCodeRelation.nullish(),
    // Computed fields added by extend-populate middleware
    hasClaimedReward: z.boolean().nullish(),
    uncollectedCommission: z.number().nullish(),
    totalCommission: z.number().nullish(),
});
/**
 * Referral code schema - extends ReferralCodeRelationSchema with relations
 */
export const ReferralCodeSchema = ReferralCodeRelationSchema.extend({
    // Computed fields (added by backend middleware)
    referralsCount: z.number().nullish(),
    // Relations
    referrer: LazyUserRelation.nullish(),
    referrals: z.array(LazyReferralRelation).nullish(),
});
/**
 * Monthly partner stats schema
 */
export const MonthlyPartnerStatsSchema = z.object({
    month: z.string(),
    referrals: z.number(),
    earnings: z.number(),
    payouts: z.number(),
});
/**
 * Partner stats schema
 */
export const PartnerStatsSchema = z.object({
    referrerType: ReferrerTypeSchema,
    totalReferrals: z.number(),
    activeReferrals: z.number(),
    totalEarnings: z.number(),
    pendingEarnings: z.number(),
    availableBalance: z.number(),
    currency: CurrencyCodeSchema,
    monthlyStats: z.array(MonthlyPartnerStatsSchema).nullish(),
});
/**
 * Discount reward schema
 */
export const DiscountRewardSchema = z.object({
    id: StrapiIdSchema,
    amount: z.number(),
    currency: CurrencyCodeSchema,
    status: RewardStatusSchema,
    expiresAt: z.string().nullish(),
    claimedAt: z.string().nullish(),
    source: z.enum(["referral", "promotion", "manual"]),
});
/**
 * Discount wallet schema
 */
export const DiscountWalletSchema = z.object({
    balance: z.number(),
    currency: CurrencyCodeSchema,
    pendingRewards: z.array(DiscountRewardSchema),
    availableRewards: z.array(DiscountRewardSchema),
    claimedRewards: z.array(DiscountRewardSchema),
});
/**
 * Payout reward schema
 */
export const PayoutRewardSchema = z.object({
    id: StrapiIdSchema,
    amount: z.number(),
    currency: CurrencyCodeSchema,
    status: RewardStatusSchema,
    source: z.enum(["referral_commission", "bonus", "manual"]),
    earnedAt: z.string(),
    availableAt: z.string().nullish(),
});
/**
 * Commission item in payouts wallet
 */
export const PayoutsWalletCommissionSchema = z.object({
    id: StrapiIdSchema,
    amount: z.number(),
    referrerType: ReferrerTypeSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    payout: z.unknown().nullish(),
});
/**
 * Payouts wallet schema - matches getReferrerUncollectedCommission response
 */
export const PayoutsWalletSchema = z.object({
    amount: z.number(),
    claimedAmount: z.number(),
    commissions: z.array(PayoutsWalletCommissionSchema),
    collectable: z.boolean(),
    uncollectableReasons: z.array(z.string()),
    processingAmount: z.number().nullish(),
});
/**
 * Rewards list schema
 */
export const RewardsListSchema = z.object({
    discountRewards: z.array(DiscountRewardSchema),
    payoutRewards: z.array(PayoutRewardSchema),
});
/**
 * Requirement schema
 */
export const RequirementSchema = z.object({
    name: z.string(),
    description: z.string(),
    met: z.boolean(),
    current: z.number().nullish(),
    required: z.number().nullish(),
});
/**
 * Partner requirements schema
 */
export const PartnerRequirementsSchema = z.object({
    referrerType: ReferrerTypeSchema,
    requirements: z.array(RequirementSchema),
    allMet: z.boolean(),
});
/**
 * Recurring requirement schema
 */
export const RecurringRequirementSchema = z.object({
    name: z.string(),
    description: z.string(),
    met: z.boolean(),
    current: z.number(),
    required: z.number(),
    period: z.enum(["monthly", "quarterly", "yearly"]),
});
/**
 * Recurring requirements schema
 */
export const RecurringRequirementsSchema = z.object({
    requirements: z.array(RecurringRequirementSchema),
    currentPeriod: z.string(),
    nextReviewDate: z.string(),
});
/**
 * Payout entity schema - extends PayoutRelationSchema with relations
 * Note: Overrides pm_amount, pm_currency, pm_status to be required
 */
export const PayoutSchema = PayoutRelationSchema.extend({
    // Override to make required
    pm_amount: z.number(),
    pm_currency: CurrencyCodeSchema,
    pm_status: z.enum(["pending", "processing", "processed", "failed", "cancelled", "expired"]),
    // Additional relations (populated)
    pm_issuerBillingData: LazyBillingDataRelation.nullish(),
    pm_recipientBillingData: LazyBillingDataRelation.nullish(),
    pm_user: LazyUserRelation.nullish(),
    commissions: z.array(LazyCommissionRelation).nullish(),
});
/**
 * Referrer stats admin schema
 */
export const ReferrerStatsAdminSchema = z.object({
    userId: StrapiIdSchema,
    email: z.string(),
    name: z.string().nullish(),
    referrerType: ReferrerTypeSchema,
    totalReferrals: z.number(),
    activeReferrals: z.number(),
    totalEarnings: z.number(),
    totalPayouts: z.number(),
    availableBalance: z.number(),
    joinedAt: z.string(),
    lastActivityAt: z.string().nullish(),
});
/**
 * Currency amount map schema (e.g., { EUR: 100, USD: 50 })
 */
const CurrencyAmountMapSchema = z.record(z.string(), z.number()).optional();
/**
 * Month totals schema for charged breakdown
 */
const MonthTotalsSchema = z.object({
    total: z.number(),
    hierarchyTotal: z.number().nullish(),
});
/**
 * Year totals schema for charged breakdown
 */
const YearTotalsSchema = z.object({
    total: z.number(),
    hierarchyTotal: z.number().nullish(),
    perMonth: z.record(z.string(), MonthTotalsSchema).nullish(),
});
/**
 * Currency charged data schema
 */
const CurrencyChargedDataSchema = z.object({
    total: z.number(),
    hierarchyTotal: z.number().nullish(),
    perYear: z.record(z.string(), YearTotalsSchema).nullish(),
});
/**
 * Referrer info in hierarchy stats
 */
const HierarchyReferrerSchema = z.object({
    id: StrapiIdSchema,
    email: z.string(),
    name: z.string().nullish(),
    referrerType: ReferrerTypeSchema.nullish(),
    isVIP: z.boolean().nullish(),
    blocked: z.boolean().nullish(),
    createdAt: z.string().nullish(),
});
/**
 * Referrer hierarchy stats schema - matches actual API response from getReferralsHierarchy
 */
export const ReferrerHierarchyStatsSchema = z.object({
    referrer: HierarchyReferrerSchema.nullish(),
    pendingRewards: z.number().nullish(),
    referredBy: StrapiIdSchema.nullish(),
    referralsCount: z.number().nullish(),
    activeBots: z.number().nullish(),
    inactiveBots: z.number().nullish(),
    activeBalance: CurrencyAmountMapSchema,
    inactiveBalance: CurrencyAmountMapSchema,
    totalProfit: CurrencyAmountMapSchema,
    totalLosses: CurrencyAmountMapSchema,
    charged: z.record(z.string(), CurrencyChargedDataSchema).nullish(),
    referredAffiliates: z.array(StrapiIdSchema).nullish(),
});
